import { Building07, RefreshCw05 } from "@untitledui/icons";
import type { MerchantDraftItem, WorkspaceMerchantItem } from "@/api/workspace-merchants";
import { MerchantLogo } from "@/components/application/merchant-logo/merchant-logo";
import { Table } from "@/components/application/table/table";
import { Badge, BadgeWithIcon } from "@/components/base/badges/badges";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { cx } from "@/utils/cx";

export const MERCHANT_SETUP_STEPS = 5;

const ANALYSING_TOOLTIP =
  "We are analysing all videos. This can take up to 48 hours to complete. We'll send you an email to let you know when this is complete.";

const createdFormat = new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" });

const COLUMNS = {
  name: "w-[36%] pl-6 pr-4",
  region: "w-[14%] px-4",
  risks: "w-[18%] px-4",
  users: "w-[14%] px-4",
  created: "w-[18%] px-4 pr-6",
};

type MerchantRow =
  | { kind: "draft"; id: string; draft: MerchantDraftItem }
  | { kind: "merchant"; id: string; merchant: WorkspaceMerchantItem };

const CountBadge = ({ value }: { value: number }) => (
  <span className="inline-flex size-6 items-center justify-center rounded-full bg-gray-100 text-xs font-medium text-secondary">
    {value}
  </span>
);

const NameCell = ({
  name,
  logoUrl,
  subline,
  badge,
  muted,
}: {
  name: string;
  logoUrl: string | null;
  subline: string;
  badge?: React.ReactNode;
  muted?: boolean;
}) => (
  <div className="flex items-center gap-3 min-w-0">
    <span className={cx("shrink-0", muted && "opacity-50")}>
      <MerchantLogo name={name} logoUrl={logoUrl} size="sm" className="bg-white object-contain ring-1 ring-secondary" />
    </span>
    <div className="flex flex-col min-w-0">
      <p className={cx("text-sm font-medium truncate", muted ? "text-tertiary" : "text-primary")}>{name}</p>
      <p className="text-sm text-tertiary truncate">{subline}</p>
    </div>
    {badge}
  </div>
);

const AnalysingBadge = () => (
  <Tooltip title={ANALYSING_TOOLTIP} placement="top">
    <TooltipTrigger className="shrink-0">
      <BadgeWithIcon type="pill-color" color="brand" size="sm" iconLeading={SpinningRefresh}>
        Analysing all videos
      </BadgeWithIcon>
    </TooltipTrigger>
  </Tooltip>
);

const SpinningRefresh = ({ className }: { className?: string }) => (
  <RefreshCw05 className={cx(className, "animate-spin [animation-duration:1.6s]")} />
);

export const WorkspaceMerchantsTable = ({
  merchants,
  draft,
}: {
  merchants: WorkspaceMerchantItem[];
  draft: MerchantDraftItem | null;
}) => {
  const rows: MerchantRow[] = [
    ...(draft ? [{ kind: "draft" as const, id: `draft-${draft.id}`, draft }] : []),
    ...merchants.map((merchant) => ({ kind: "merchant" as const, id: merchant.id, merchant })),
  ];

  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-16">
        <div className="flex items-center justify-center size-14 rounded-full bg-tertiary">
          <Building07 className="size-6 text-secondary" />
        </div>
        <p className="text-sm text-tertiary max-w-xs text-center">Merchants you add will appear here.</p>
      </div>
    );
  }

  return (
    <Table aria-label="Merchants" className="table-fixed min-w-0">
      <Table.Header>
        <Table.Head id="name" label="Name" isRowHeader className={COLUMNS.name} />
        <Table.Head id="region" label="Region" className={COLUMNS.region} />
        <Table.Head id="risks" label="Safety models" className={COLUMNS.risks} />
        <Table.Head id="users" label="Users" className={COLUMNS.users} />
        <Table.Head id="created" label="Created" className={COLUMNS.created} />
      </Table.Header>

      <Table.Body items={rows}>
        {(row) => {
          if (row.kind === "draft") {
            const { draft } = row;
            return (
              <Table.Row id={row.id} className="bg-secondary_subtle">
                <Table.Cell className={COLUMNS.name}>
                  <NameCell
                    name={draft.data.details?.name || "New merchant"}
                    logoUrl={draft.logoUrl}
                    subline={draft.data.details?.handle ? `@${draft.data.details.handle}` : "Not named yet"}
                    muted
                    badge={
                      <Badge type="pill-color" color="warning" size="sm" className="shrink-0 whitespace-nowrap">
                        Setup in progress · step {draft.step} of {MERCHANT_SETUP_STEPS}
                      </Badge>
                    }
                  />
                </Table.Cell>
                <Table.Cell className={COLUMNS.region}>
                  <span className="text-sm text-tertiary">—</span>
                </Table.Cell>
                <Table.Cell className={COLUMNS.risks}>
                  <span className="text-sm text-tertiary">—</span>
                </Table.Cell>
                <Table.Cell className={COLUMNS.users}>
                  <span className="text-sm text-tertiary">—</span>
                </Table.Cell>
                <Table.Cell className={COLUMNS.created}>
                  <span className="text-sm text-tertiary">{createdFormat.format(new Date(draft.createdAt))}</span>
                </Table.Cell>
              </Table.Row>
            );
          }

          const { merchant } = row;
          const isAnalysing = merchant.onboardingStatus === "scanning";
          return (
            <Table.Row id={row.id} aria-disabled={isAnalysing || undefined}>
              <Table.Cell className={COLUMNS.name}>
                <NameCell
                  name={merchant.name}
                  logoUrl={merchant.logoUrl}
                  subline={`ID: ${merchant.id}`}
                  muted={isAnalysing}
                  badge={isAnalysing ? <AnalysingBadge /> : undefined}
                />
              </Table.Cell>
              <Table.Cell className={COLUMNS.region}>
                {merchant.region ? (
                  <Badge type="pill-color" color="gray" size="sm">
                    {merchant.region}
                  </Badge>
                ) : null}
              </Table.Cell>
              <Table.Cell className={COLUMNS.risks}>
                {isAnalysing ? (
                  <span className="text-sm text-tertiary">—</span>
                ) : (
                  <div className="flex items-center gap-2">
                    <CountBadge value={merchant.riskCount} />
                    {merchant.hasCustomRisks && (
                      <Badge type="pill-color" color="brand" size="sm">
                        Custom
                      </Badge>
                    )}
                  </div>
                )}
              </Table.Cell>
              <Table.Cell className={COLUMNS.users}>
                <CountBadge value={merchant.userCount} />
              </Table.Cell>
              <Table.Cell className={COLUMNS.created}>
                <span className="text-sm text-tertiary">{createdFormat.format(new Date(merchant.createdAt))}</span>
              </Table.Cell>
            </Table.Row>
          );
        }}
      </Table.Body>
    </Table>
  );
};
